import { mdiAccount } from '@mdi/js'
import Icon from '@mdi/react'
import Paper from '@mui/material/Paper'
import { SocialIcon } from 'components/SocialIcon'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { NavLink } from 'react-router'
import styles from './SocialProfileCard.pcss'
import { BRAND_LINKEDIN, PostDestination } from 'shared'

const LINKEDIN_ICON_SIZE = 22

const LINKEDIN_PATH_1 = 'M11.185,0.08C5.004,0.08,0.001,5.092,0,11.259c0,6.173,5.003,11.184,11.186,11.184'
  + 'c6.166,0,11.176-5.011,11.176-11.184 C22.362,5.091,17.351,0.08,11.185,0.08z'

const LINKEDIN_PATH_2 = 'M69.925,29.383c-9.382,0-15.673,5.144-18.248,10.022h-0.258v-8.479H32.921H32.92v62.053h19.27V62.281'
  + ' c0-8.093,1.541-15.932,11.575-15.932c9.89,0,10.022,9.256,10.022,16.451v30.178H93.06V58.942'
  + ' C93.06,42.235,89.455,29.383,69.925,29.383z'

function LinkedInIcon() {
  return (
    <svg
      className={styles['s-icon']}
      width={LINKEDIN_ICON_SIZE}
      height={LINKEDIN_ICON_SIZE}
      viewBox="0 0 93.06 93.06"
      fill="#ffffff"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={LINKEDIN_PATH_1} />
      <rect x="1.538" y="30.926" width="19.287" height="62.054" />
      <path d={LINKEDIN_PATH_2} />
    </svg>
  )
}

export interface SocialProfileCardProps {
  profile: PostDestination
  navigation?: string
  style?: React.CSSProperties
  className?: string
  postsCount?: number
  onClick?: (profile: PostDestination) => void
}

const LOW_POSTS_THRESHOLD = 5

function getPostsBadgeTier(count: number): 'posts-badge-empty' | 'posts-badge-low' | 'posts-badge-high' {
  if (count === 0) return 'posts-badge-empty'
  if (count <= LOW_POSTS_THRESHOLD) return 'posts-badge-low'
  return 'posts-badge-high'
}

export function SocialProfileCard(props: SocialProfileCardProps) {
  const { profile, navigation, style, className, postsCount, onClick } = props
  const ref = React.useRef<HTMLDivElement | null>(null)
  const cardElementRef = React.useRef<HTMLElement | null>(null)

  const onTransitionEnd = React.useCallback((e: any) => {
    const classes = e.target.classList
    const currentScale = (e.target.getBoundingClientRect().width / e.target.offsetWidth)
    const isCollapsed = currentScale.toFixed(1) === '1.0'
    if (classes.contains(styles.raised) && isCollapsed) {
      classes.remove(styles.raised)
    }
  }, [])

  React.useEffect(() => {
    cardElementRef.current = ref.current
    const el = cardElementRef.current
    if (el) {
      el.addEventListener('transitionend', onTransitionEnd)
    }
    return () => {
      if (el) {
        el.removeEventListener('transitionend', onTransitionEnd)
      }
    }
  }, [onTransitionEnd])

  const onMouseEnter = React.useCallback(() => {
    const el = cardElementRef.current
    if (el) {
      el.classList.add(styles.raised)
    }
  }, [])

  const handleClick = React.useCallback(() => {
    if (onClick) {
      onClick(profile)
    }
  }, [onClick, profile])

  const { image, name, type } = profile
  const [imageFailed, setImageFailed] = React.useState(false)

  React.useEffect(() => {
    setImageFailed(false)
    if (!image) {
      return
    }
    const img = new Image()
    img.onerror = () => setImageFailed(true)
    img.src = image
  }, [image])

  const showFallback = !image || imageFailed
  const imageStyles: React.CSSProperties = showFallback ? {} : { backgroundImage: `url('${image}')` }

  const imageEl = (
    <div className={`${styles['profile-image']} ${showFallback ? styles['profile-image-fallback'] : ''}`} style={imageStyles}>
      {showFallback && <Icon path={mdiAccount} color="#fff" size="80%" />}
    </div>
  )

  return (
    <div
      className={`${styles.container} ${className || ''}`}
      style={style}
      data-testid="social-profile-card"
    >
      <Paper
        className={styles.card}
        ref={ref}
        onMouseEnter={onMouseEnter}
        onClick={handleClick}
      >
        {navigation ? (
          <NavLink to={navigation} className={styles['card-link']}>{imageEl}</NavLink>
        ) : (
          <div className={styles['card-link']}>{imageEl}</div>
        )}
        {postsCount !== undefined && (
          <span className={`${styles['posts-badge']} ${styles[getPostsBadgeTier(postsCount)]}`}>
            <FormattedMessage id="post.labels.posts-count" values={{ count: postsCount }} />
          </span>
        )}
        <span className={`${styles['social-badge']} ${styles[type]}`}>
          {type === BRAND_LINKEDIN ? (
            <LinkedInIcon />
          ) : (
            <SocialIcon
              icon={type}
              className={styles['s-icon']}
              size="35px"
              circle
            />
          )}
        </span>
      </Paper>
      <p className={`${styles.name} text-ellipsis`} title={name}>{name}</p>
    </div>
  )
}

export default SocialProfileCard
